import { compterParStatut, trierLignes } from '@imp/shared/liste-triee';
import { messageErreur } from '@imp/shared/message-erreur';
import { filtrerRecherche } from '@imp/shared/recherche';
import { useQuery } from '@tanstack/react-query';
import { FileSpreadsheet, Landmark } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { BarreRecherche } from '@/components/BarreRecherche';
import { EnteteTriable } from '@/components/EnteteTriable';
import { PastillesStatut } from '@/components/PastillesStatut';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useEtatListe } from '@/lib/useEtatListe';
import { formatEuro } from '@/modules/catalogue/api';
import { contactDisplayName, listContacts } from '@/modules/clients/api';
import { FACTURE_STATUT_LABELS, type FactureListItem, listFactures } from '@/modules/factures/api';

const STATUT_CLASSES: Record<string, string> = {
  brouillon: 'bg-muted text-muted-foreground',
  emise: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  envoyee: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  partiellement_payee: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  payee: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  en_retard: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  litige: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  annulee_avoir: 'bg-muted text-muted-foreground',
};

/** L'ordre dans lequel une facture avance : c'est aussi celui des pastilles. */
const ORDRE_STATUTS = Object.keys(FACTURE_STATUT_LABELS);

const CLES_TRI = [
  'numero',
  'client',
  'objet',
  'date',
  'echeance',
  'statut',
  'ttc',
  'paye',
] as const;
type CleTri = (typeof CLES_TRI)[number];

/** `2026-10-06` → `06/10/2026` */
function dateFr(iso: string | null): string {
  return iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR') : '—';
}

export function FacturesListPage() {
  const [q, setQ] = useState('');
  // Par défaut, la plus récente en tête.
  const liste = useEtatListe<CleTri>(CLES_TRI, { cle: 'date', sens: 'desc' });
  const facturesQ = useQuery({ queryKey: ['factures'], queryFn: listFactures });
  const contactsQ = useQuery({ queryKey: ['contacts'], queryFn: listContacts });
  const contactMap = useMemo(
    () => new Map((contactsQ.data ?? []).map((c) => [c.id, c])),
    [contactsQ.data],
  );
  const factures = useMemo(() => facturesQ.data ?? [], [facturesQ.data]);
  // Recherche universelle : on fouille tout ce que la ligne affiche.
  const filtered = useMemo(
    () =>
      filtrerRecherche(factures, q, (f) => {
        const c = contactMap.get(f.contact_id);
        return [
          f.numero,
          c ? contactDisplayName(c) : null,
          f.objet,
          f.date_echeance,
          FACTURE_STATUT_LABELS[f.statut] ?? f.statut,
          f.kind === 'avoir' ? 'avoir' : null,
        ];
      }),
    [factures, q, contactMap],
  );
  const nomClient = (f: FactureListItem): string => {
    const c = contactMap.get(f.contact_id);
    return c ? contactDisplayName(c) : '';
  };
  const comptes = compterParStatut(filtered, (f) => f.statut, ORDRE_STATUTS);
  const affiches = trierLignes(
    liste.statut === null ? filtered : filtered.filter((f) => f.statut === liste.statut),
    liste.tri,
    {
      // Un brouillon n'a pas encore de numéro : il ne passe pas devant les vraies.
      numero: (f) => (f.numero.startsWith('BROUILLON-') ? null : f.numero),
      client: nomClient,
      objet: (f) => f.objet,
      date: (f) => f.date_emission,
      echeance: (f) => f.date_echeance,
      statut: (f) => ORDRE_STATUTS.indexOf(f.statut),
      ttc: (f) => (f.kind === 'avoir' ? -1 : 1) * Number(f.montant_ttc),
      paye: (f) => Number(f.montant_paye),
    },
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Factures</h1>
          <p className="text-muted-foreground">
            Facturation conforme NF525 — numérotation séquentielle, factures verrouillées après
            émission. Créez une facture depuis un devis.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link to="/factures/reglements">
              <Landmark className="h-5 w-5" /> Règlements
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/factures/export-comptable">
              <FileSpreadsheet className="h-5 w-5" /> Export comptable
            </Link>
          </Button>
        </div>
      </div>

      {factures.length > 0 ? (
        <BarreRecherche
          valeur={q}
          onChange={setQ}
          quoi="une facture"
          champs="numéro, client, objet, statut"
          resultats={filtered.length}
          total={factures.length}
        />
      ) : null}
      {factures.length > 0 ? (
        <PastillesStatut
          comptes={comptes}
          libelles={FACTURE_STATUT_LABELS}
          classes={STATUT_CLASSES}
          actif={liste.statut}
          total={filtered.length}
          onChoisir={liste.choisirStatut}
          libelleTous="Toutes"
        />
      ) : null}

      {facturesQ.isLoading ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : facturesQ.isError ? (
        <p className="text-destructive">Erreur : {messageErreur(facturesQ.error)}</p>
      ) : affiches.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <p className="text-muted-foreground">
            {factures.length === 0
              ? 'Aucune facture. Convertissez un devis signé.'
              : 'Aucun résultat.'}
          </p>
          {factures.length === 0 ? (
            <Link to="/devis" className="text-primary text-sm hover:underline">
              Voir les devis
            </Link>
          ) : null}
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground border-b text-left text-xs uppercase">
              <tr>
                <EnteteTriable
                  cle="numero"
                  libelle="Numéro"
                  tri={liste.tri}
                  onTrier={liste.trierPar}
                  sensNaturel="desc"
                />
                <EnteteTriable
                  cle="client"
                  libelle="Client"
                  tri={liste.tri}
                  onTrier={liste.trierPar}
                />
                <EnteteTriable
                  cle="objet"
                  libelle="Objet"
                  tri={liste.tri}
                  onTrier={liste.trierPar}
                />
                <EnteteTriable
                  cle="date"
                  libelle="Date"
                  tri={liste.tri}
                  onTrier={liste.trierPar}
                  sensNaturel="desc"
                />
                <EnteteTriable
                  cle="echeance"
                  libelle="Échéance"
                  tri={liste.tri}
                  onTrier={liste.trierPar}
                />
                <EnteteTriable
                  cle="statut"
                  libelle="Statut"
                  tri={liste.tri}
                  onTrier={liste.trierPar}
                />
                <EnteteTriable
                  cle="ttc"
                  libelle="Total TTC"
                  tri={liste.tri}
                  onTrier={liste.trierPar}
                  sensNaturel="desc"
                  aDroite
                />
                <EnteteTriable
                  cle="paye"
                  libelle="Payé"
                  tri={liste.tri}
                  onTrier={liste.trierPar}
                  sensNaturel="desc"
                  aDroite
                />
              </tr>
            </thead>
            <tbody>
              {affiches.map((f) => {
                const c = contactMap.get(f.contact_id);
                const provisoire = f.numero.startsWith('BROUILLON-');
                return (
                  <tr key={f.id} className="hover:bg-muted/40 border-b last:border-0">
                    <td className="px-4 py-3 font-mono text-xs">
                      <Link to={`/factures/${f.id}`} className="text-primary hover:underline">
                        {provisoire ? '— (brouillon)' : f.numero}
                      </Link>
                      {f.kind === 'avoir' ? (
                        <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                          Avoir
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">{c ? contactDisplayName(c) : '—'}</td>
                    <td className="text-muted-foreground px-4 py-3">{f.objet}</td>
                    <td className="text-muted-foreground whitespace-nowrap px-4 py-3">
                      {dateFr(f.date_emission)}
                    </td>
                    <td className="text-muted-foreground whitespace-nowrap px-4 py-3">
                      {dateFr(f.date_echeance)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${STATUT_CLASSES[f.statut] ?? 'bg-muted text-muted-foreground'}`}
                      >
                        {FACTURE_STATUT_LABELS[f.statut] ?? f.statut}
                      </span>
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-medium ${f.kind === 'avoir' ? 'text-money-out' : ''}`}
                    >
                      {f.kind === 'avoir' ? '−' : ''}
                      {formatEuro(f.montant_ttc)}
                    </td>
                    <td className="text-muted-foreground px-4 py-3 text-right">
                      {formatEuro(f.montant_paye)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
